import { Fragment } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { t } from "@wr/i18n";
import privacySource from "../content/legal/privacy.md?raw";
import termsSource from "../content/legal/terms.md?raw";
import { parseLegalDocument, type Block, type Inline } from "../lib/legal-doc";

/**
 * `/terms` and `/privacy` — the reviewed finals, rendered verbatim.
 *
 * **The documents are content, not copy.** They do not live in `@wr/i18n` and
 * they are not written here: `apps/web/src/content/legal/*.md` are byte-for-byte
 * copies of the post-attorney-review finals in
 * `techdad-company/projects/wordy-royale/legal/` (`tos-new.md`,
 * `privacy-policy-new.md`), which stays the canonical origin. Architecture §14
 * rule 10 ("every user-visible string resolves through the catalogue") is about
 * *product copy* — a legal instrument that a lawyer signed off on is the one
 * thing that must never be paraphrased by a translator or trimmed to a
 * character budget. The two strings on this screen that **are** product copy
 * (the back link, the pair of document titles used by the drawer) still come
 * from the catalogue.
 *
 * **No new design surface** (§0): the page is the existing type ramp on the
 * existing canvas, in a reading-measure column. `parseLegalDocument` turns the
 * Markdown into elements; nothing here decides what the documents say.
 *
 * **This screen is deliberately outside `AppShell`** — the routes predate this
 * ticket and are reachable pre-auth, from the sign-up attestation line, which
 * is exactly when a player has no session and there is no chrome to hang. The
 * document's own `#` heading is therefore the screen's only `<h1>` (S2 §20.2's
 * single-h1 rule) and carries `data-route-heading` for §15 rule 11.
 */

const DOCUMENTS = {
  terms: parseLegalDocument(termsSource),
  privacy: parseLegalDocument(privacySource),
} as const;

export type LegalDocumentName = keyof typeof DOCUMENTS;

/**
 * **The flag `LegalLink` sets, and the only thing this screen reads to choose a
 * mode** (Ivy §22.5). A *declaration by the opener*, never a detection: browsers
 * apply implicit `noopener` to `target="_blank"` and we send `rel="noreferrer"`
 * on top of it, so `window.opener` is unusable; and `history.length` is 1 for a
 * fresh app-opened tab and for a fresh deep link alike, so it cannot tell them
 * apart either. **Do not sniff, and do not switch to `rel="opener"` to make
 * sniffing work.**
 */
export const LEGAL_FROM_APP = "from=app";

/** How long after `window.close()` the refusal fallback fires (§22.5). */
const CLOSE_FALLBACK_MS = 200;

type ExitMode = "close" | "navigate";

export function LegalScreen({ doc }: { doc: LegalDocumentName }) {
  const blocks = DOCUMENTS[doc];
  const [params] = useSearchParams();
  /**
   * **The flag is read and deliberately not stripped** (§22.5). A cleaned URL
   * loses the mode on reload, and a reloaded app-opened tab is still a tab that
   * should close. If the flag escapes into a shared link, the fallback below
   * makes that harmless — the worst case is today's behaviour.
   */
  const mode: ExitMode = params.get("from") === "app" ? "close" : "navigate";

  return (
    <main className="wr-screen wr-doc" data-testid="legal" data-doc={doc}>
      {/**
       * **The way out, at the top as well as the bottom** (T-243 item 2, Aaron's
       * punch list: *"the existing bottom control alone forces a full scroll"*),
       * **as one control with two modes** (Ivy §22.5, following T-244's N1).
       *
       * Both exits always carry the same label and the same behaviour — §22.1's
       * *"the same action offered twice, not two actions"*. **Since Ivy's
       * UI-review ruling §1.2 (2026-08-19) they also carry the same width and
       * the same variant**: this paragraph used to end *"only their width and
       * variant differ"*, and it does not any more. Two actions that are the
       * same action should not have two weights — see `LegalExit` for why the
       * asymmetry's own reason expired.
       *
       * Two controls with the same accessible name on one page is correct here
       * and not a duplicate-label defect. The `data-testid`s tell them apart for
       * QA — they are **stable across both modes** by ruling, selector stability
       * beating naming purity — and `data-exit-mode` says which branch is live.
       */}
      <div className="wr-doc__column wr-doc__actions wr-doc__actions--top">
        <LegalExit mode={mode} position="top" />
      </div>

      <article className="wr-doc__column" data-testid="legal-document">
        {blocks.map((block, index) => (
          <BlockView key={index} block={block} />
        ))}
      </article>

      <div className="wr-doc__column wr-doc__actions">
        <LegalExit mode={mode} position="bottom" />
      </div>
    </main>
  );
}

/**
 * **One control, two modes — and the mode says what the control will do.**
 *
 * | Entry path | Label | Element | Action |
 * |---|---|---|---|
 * | `?from=app` (attestation, drawer — the dominant path) | `common.close` | `<button>` | `window.close()`, with the floor below |
 * | Deep link, bookmark, pasted URL, search result | `common.backToLogin` | `<Link to="/login">` | Navigate |
 *
 * The drawer is reachable **during a game**, so `LegalLink` opens a new tab and
 * that is LOCKED (§22.5): a same-tab navigation to `/terms` would unmount the
 * game screen and drop a live room the player has coins in. Which means the
 * dominant exit is closing a tab we opened, and the surface the player left is
 * still sitting behind it, untouched.
 *
 * **`window.close()` may be refused, so it always has a floor.** Engines differ
 * on whether a script may close a tab a link opened. The navigation is scheduled
 * unconditionally at 200 ms: a successful close tears the document down and the
 * timer never fires; a refusal lands the player on `/login`, which is exactly
 * today's behaviour. So the worst case of this change is the current behaviour,
 * never worse — and a button that visibly does nothing is designed out. No
 * cleanup, no error UI, no toast.
 */
function LegalExit({ mode, position }: { mode: ExitMode; position: "top" | "bottom" }) {
  const navigate = useNavigate();
  const testId = position === "top" ? "legal-back-top" : "legal-back-bottom";
  /**
   * **One control, drawn twice — Ivy's UI-review ruling §1.2 (2026-08-19),
   * which AMENDS §22.1's width table and §22.5 N2's variant table.**
   *
   * Aaron: *"Make the close at the bottom the same style as the close button at
   * the top."* The old branch read `top: secondary sm intrinsic` /
   * `bottom: ghost md --full`, and its reasoning was: *"the top exit is a
   * control among content and needs an edge to read as one; the bottom exit is
   * the last object on the page and cannot be mistaken for anything, so a
   * full-width `secondary` there would read as the page's primary CTA."*
   *
   * Ivy reversed her own asymmetry, and the argument that changed is the second
   * clause: **the reason for it was consumed by the fix that followed it.**
   * §22.1 gave the bottom control its width because it was the only *bounded*
   * object on the page while the top one read as prose; §22.5 N2 then gave the
   * top control a boundary. Once both are bordered chips the width difference
   * stops being meaningful and starts looking like an oversight — which is
   * exactly how it was reported.
   *
   * *"On a legal document, nothing is a CTA"* (§22.5 N2) survives and is better
   * served: an intrinsic-width `secondary` chip is visibly not a page CTA,
   * whereas a full-width control at the end of a document is the one shape that
   * reads as one.
   *
   * So there is **no branch left** — `position` now decides the `data-testid`
   * and nothing else, which is the ruling's title made literal.
   */
  const className = "wr-btn wr-btn--secondary wr-btn--sm";
  /** Leading, `aria-hidden`, riding `.wr-btn`'s own `gap` (Lobby.tsx's precedent). */
  const glyph = <span aria-hidden="true">{mode === "close" ? "×" : "←"}</span>;

  if (mode === "close") {
    return (
      <button
        type="button"
        className={className}
        data-testid={testId}
        data-exit-mode="close"
        onClick={() => {
          window.close();
          window.setTimeout(() => navigate("/login", { replace: true }), CLOSE_FALLBACK_MS);
        }}
      >
        {glyph}
        {t("common.close")}
      </button>
    );
  }

  return (
    <Link className={className} to="/login" data-testid={testId} data-exit-mode="navigate">
      {glyph}
      {t("common.backToLogin")}
    </Link>
  );
}

function BlockView({ block }: { block: Block }) {
  if (block.kind === "heading") {
    return block.level === 1 ? (
      <h1 className="wr-display-lg wr-doc__h1" data-route-heading>
        <InlineView content={block.content} />
      </h1>
    ) : (
      <h2 className="wr-display-sm wr-doc__h2">
        <InlineView content={block.content} />
      </h2>
    );
  }

  if (block.kind === "paragraph") {
    return (
      <p className="wr-body-md wr-doc__p">
        <InlineView content={block.content} />
      </p>
    );
  }

  if (block.kind === "list") {
    return (
      <ul className="wr-doc__list">
        {block.items.map((item, index) => (
          <li key={index} className="wr-body-md">
            <InlineView content={item} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="wr-doc__table-wrap">
      <table className="wr-doc__table">
        <thead>
          <tr>
            {/*
              `wr-body-sm`, not `wr-label-sm`: that step carries
              `text-transform: uppercase`, and a document rendered verbatim does
              not get its column headings recased by a stylesheet.
            */}
            {block.head.map((cell, index) => (
              <th key={index} className="wr-body-sm" scope="col">
                <InlineView content={cell} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="wr-body-sm">
                  <InlineView content={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InlineView({ content }: { content: Inline[] }) {
  return (
    <>
      {content.map((node, index) => (
        <Fragment key={index}>
          {node.kind === "strong" ? (
            <strong className="wr-doc__strong">{node.value}</strong>
          ) : node.kind === "code" ? (
            <code className="wr-mono-sm wr-doc__code">{node.value}</code>
          ) : node.kind === "link" ? (
            /* Only external destinations occur in these documents today; an
               in-app one would still be correct as an anchor, and `noreferrer`
               costs nothing on a same-origin href. */
            <a className="wr-link" href={node.href} rel="noreferrer">
              {node.value}
            </a>
          ) : (
            node.value
          )}
        </Fragment>
      ))}
    </>
  );
}
